/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Typography2 } from '@coursera/cds-core';
import { ChevronDownIcon } from '@coursera/cds-icons';

import { useSideNavCollapse } from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavCollapseProvider';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';
import DefaultOrganizationLogo from 'bundles/enterprise-legacy-learner-home/components/DefaultOrganizationLogo';

import _t from 'i18n!nls/enterprise-admin-navigation';

const styles = {
  wrapper: css`
    border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
  `,
  pill: css`
    width: 100%;
    height: 44px;
    display: flex;
    align-items: center;
    padding: 0 var(--cds-spacing-150);
    background: transparent;
    border: none;
    border-radius: var(--cds-border-radius-100);
    cursor: pointer;
    text-align: left;
    transition: background-color 200ms ease;

    &:hover,
    &:focus-visible {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
    }
  `,
  pillOpen: css`
    background-color: var(--cds-color-interactive-background-primary-hover-weak);
  `,
  pillCollapsed: css`
    justify-content: center;
    padding: 0 var(--cds-spacing-150);
  `,
  logoContainer: css`
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--cds-border-radius-50);
    overflow: hidden;

    & img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `,
  textContainer: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    text-align: left;
    overflow: hidden;
    transition: opacity 200ms ease, width 200ms ease, margin 200ms ease;
    opacity: 1;
    width: auto;
    margin-left: var(--cds-spacing-150);
  `,
  textContainerHidden: css`
    opacity: 0;
    width: 0;
    margin-left: 0;
  `,
  truncate: css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  chevron: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    color: var(--cds-color-neutral-primary-weak);
    transition: transform 200ms ease, opacity 200ms ease, width 200ms ease;
    transform: rotate(-90deg);
    opacity: 1;
  `,
  chevronOpen: css`
    transform: rotate(0deg);
  `,
  chevronHidden: css`
    opacity: 0;
    width: 0;
  `,
};

type Props = {
  thirdPartyOrganization: AdminDashboardOrg;
  isOpen: boolean;
  onClick: () => void;
  popupId?: string;
};

export const AdminProfilePill = ({
  thirdPartyOrganization,
  isOpen,
  onClick,
  popupId = 'org-switcher-popup',
}: Props) => {
  const { isSideNavCollapsed } = useSideNavCollapse();
  const { name, squareLogo, rectangularLogo, primaryColor, iconColor } = thirdPartyOrganization;
  const orgLogo = squareLogo || rectangularLogo;

  const profileImage = orgLogo ? (
    <Imgix alt="" src={orgLogo} maxWidth={24} maxHeight={24} />
  ) : (
    <DefaultOrganizationLogo size={24} shape="square" thirdPartyOrganization={{ name, primaryColor, iconColor }} />
  );

  // When collapsed, the visible label is gone — bake the org name into the aria-label so
  // screen readers and keyboard users still know which org context they're switching from.
  const ariaLabel = isSideNavCollapsed
    ? _t('Open organization menu for #{orgName}', { orgName: name })
    : _t('Open organization menu');

  return (
    <div css={styles.wrapper}>
      <button
        type="button"
        onClick={onClick}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={popupId}
        aria-label={ariaLabel}
        css={[styles.pill, isOpen && styles.pillOpen, isSideNavCollapsed && styles.pillCollapsed]}
      >
        <div css={styles.logoContainer}>{profileImage}</div>
        <div css={[styles.textContainer, isSideNavCollapsed && styles.textContainerHidden]}>
          <Typography2 variant="actionSecondary" component="span" color="body" css={styles.truncate}>
            {_t('Organization Admin')}
          </Typography2>
          <Typography2 variant="bodyTertiary" component="span" color="supportText" css={styles.truncate}>
            {name}
          </Typography2>
        </div>
        <span css={[styles.chevron, isOpen && styles.chevronOpen, isSideNavCollapsed && styles.chevronHidden]}>
          <ChevronDownIcon size="medium" />
        </span>
      </button>
    </div>
  );
};

export default AdminProfilePill;
